import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Button, IconButton, InputAdornment, Alert } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import GoogleIcon from '@mui/icons-material/Google';
import './Login.css';
import { useFirebase } from '../../context/FirebaseContext';
import { getFirebaseErrorMessage } from '../../utils/firebaseErrors';
import logger from '../../utils/logger';
import { analytics } from '../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { isCodexUi } from '../../utils/codexEnv';

const Login = () => {
  const navigate = useNavigate();
  const { signInWithEmail, signInWithGoogle } = useFirebase();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!isCodexUi) {
      logEvent(analytics, 'login_attempt', { method: 'email' });
    }

    try {
      await signInWithEmail(formData.email, formData.password);
      if (!isCodexUi) {
        logEvent(analytics, 'login_success', { method: 'email' });
      }
      navigate('/');
    } catch (err) {
      logger.error('Login error:', err);
      if (!isCodexUi) {
        logEvent(analytics, 'login_failed', { method: 'email', error_code: err.code });
      }
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    if (!isCodexUi) {
      logEvent(analytics, 'login_attempt', { method: 'google' });
    }

    try {
      await signInWithGoogle();
      if (!isCodexUi) {
        logEvent(analytics, 'login_success', { method: 'google' });
      }
      navigate('/');
    } catch (err) {
      logger.error('Google sign in error:', err);
      if (!isCodexUi) {
        logEvent(analytics, 'login_failed', { method: 'google', error_code: err.code });
      }
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="ntb-copilot-login-container">
      <Box className="ntb-copilot-login-card">
        <Box className="ntb-copilot-login-header">
          <Typography variant="h4" component="h1">Welcome back</Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Please enter your details
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="ntb-copilot-login-form">
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter your email"
            variant="outlined"
            required
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleChange}
            placeholder="Enter your password"
            variant="outlined"
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box className="ntb-copilot-forgot-password">
            <Link to="/forgot-password">Forgot password?</Link>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="ntb-copilot-login-btn"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>

          <Box className="ntb-copilot-divider">
            <span>Or</span>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            className="ntb-copilot-google-btn"
            onClick={handleGoogleSignIn}
            disabled={loading}
          >
            Sign in with Google
          </Button>

          <Box className="ntb-copilot-signup-link">
            <Typography variant="body2">
              Don't have an account? <Link to="/signup">Sign up</Link>
            </Typography>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default Login; 
